/*
 * Formulario da integracao com o HubSoft: credenciais, teste de conexao e
 * classificacao dos status de servico.
 *
 * A credencial mora no banco, cifrada, e nao no `.env`: o provedor troca senha
 * de API sem avisar, e exigir redeploy para isso deixaria o portal recusando
 * cliente ate alguem com acesso ao servidor aparecer.
 *
 * A API nunca devolve segredo — so se ele existe. Por isso o campo de senha
 * aqui e sempre um campo vazio: em branco significa "mantem o que esta
 * gravado", e apagar de verdade e um botao separado. Uma tela que reexibisse a
 * senha transformaria qualquer acesso de leitura ao painel em acesso a
 * credencial do provedor.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiRequestError, apiRequest } from '../lib/api';
import type { DecisaoDeStatus, IntegrationSettingsDto } from '../lib/types';
import { Alert, Button, Card, Field, Spinner } from './ui';

type Rascunho = {
  baseUrl: string;
  authType: string;
  clientId: string;
  username: string;
  timeoutMs: string;
  cacheTtlSeconds: string;
  isActive: boolean;
};

/** Classificacao de um status, na forma que a tela edita. */
type LinhaDeStatus = {
  status: string;
  decisao: DecisaoDeStatus | '';
  sugestao: DecisaoDeStatus | null;
};

interface Props {
  podeEscrever: boolean;
  /** Avisa o pai a cada leitura, para a lista de integracoes refletir o estado. */
  onEstado?: (dto: IntegrationSettingsDto) => void;
}

function paraRascunho(dto: IntegrationSettingsDto): Rascunho {
  return {
    baseUrl: dto.baseUrl,
    authType: dto.authType,
    clientId: dto.clientId ?? '',
    username: dto.username ?? '',
    timeoutMs: String(dto.timeoutMs),
    cacheTtlSeconds: String(dto.cacheTtlSeconds),
    isActive: dto.isActive,
  };
}

/**
 * Monta as linhas de status a partir da uniao entre o que ja foi visto e o que
 * ja esta classificado.
 *
 * A uniao importa: o PUT substitui o mapa inteiro. Montar a tabela so com
 * `statusVistos` faria qualquer classificacao de um status que ainda nao
 * reapareceu sumir no proximo salvamento, sem aviso.
 */
function paraLinhas(dto: IntegrationSettingsDto): LinhaDeStatus[] {
  const porStatus = new Map<string, LinhaDeStatus>();

  for (const visto of dto.statusVistos) {
    porStatus.set(visto.status, {
      status: visto.status,
      decisao: visto.decisao ?? '',
      sugestao: visto.sugestao,
    });
  }

  for (const [status, decisao] of Object.entries(dto.statusMap)) {
    if (!porStatus.has(status)) porStatus.set(status, { status, decisao, sugestao: null });
  }

  return [...porStatus.values()].sort((a, b) => a.status.localeCompare(b.status, 'pt-BR'));
}

function quando(iso: string | null): string {
  if (!iso) return 'nunca';
  return new Date(iso).toLocaleString('pt-BR');
}

export function HubsoftIntegrationEditor({ podeEscrever, onEstado }: Props) {
  const [config, setConfig] = useState<IntegrationSettingsDto | null>(null);
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [linhas, setLinhas] = useState<LinhaDeStatus[]>([]);
  /* Segredos digitados agora. Vazio = manter o gravado; nunca vem no GET. */
  const [clientSecret, setClientSecret] = useState('');
  const [password, setPassword] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [teste, setTeste] = useState<{ ok: boolean; erro: string | null } | null>(null);

  useEffect(() => {
    let ativo = true;

    apiRequest<IntegrationSettingsDto>('/admin/integrations/hubsoft')
      .then((dto) => {
        if (!ativo) return;
        aplicar(dto);
      })
      .catch((caught: unknown) => {
        if (!ativo) return;
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, []);

  function aplicar(dto: IntegrationSettingsDto) {
    setConfig(dto);
    setRascunho(paraRascunho(dto));
    setLinhas(paraLinhas(dto));
    setClientSecret('');
    setPassword('');
    onEstado?.(dto);
  }

  async function salvar() {
    if (!rascunho) return;

    setSalvando(true);
    setErro(null);
    setAviso(null);

    try {
      const corpo: Record<string, unknown> = {
        baseUrl: rascunho.baseUrl.trim(),
        authType: rascunho.authType,
        clientId: rascunho.clientId.trim() || null,
        username: rascunho.username.trim() || null,
        timeoutMs: Number(rascunho.timeoutMs) || 5_000,
        cacheTtlSeconds: Number(rascunho.cacheTtlSeconds) || 0,
        isActive: rascunho.isActive,
        statusMap: Object.fromEntries(
          linhas
            .filter((linha) => linha.decisao !== '')
            .map((linha) => [linha.status, linha.decisao]),
        ),
      };

      /*
       * Segredo so viaja quando foi digitado. Mandar string vazia junto com os
       * outros campos apagaria a credencial a cada "Salvar" — o portal pararia
       * de autorizar e ninguem ligaria uma coisa a outra.
       */
      if (clientSecret) corpo.clientSecret = clientSecret;
      if (password) corpo.password = password;

      aplicar(
        await apiRequest<IntegrationSettingsDto>('/admin/integrations/hubsoft', {
          method: 'PUT',
          body: corpo,
        }),
      );
      setAviso('Configuração salva.');
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  async function apagarSegredo(qual: 'clientSecret' | 'password') {
    const rotulo = qual === 'clientSecret' ? 'o client secret' : 'a senha';
    if (
      !window.confirm(
        `Apagar ${rotulo} gravado? A integração para de autorizar até você informar outro.`,
      )
    ) {
      return;
    }

    setErro(null);
    try {
      // String vazia e o que o backend le como "apagar", ao contrario do ausente.
      aplicar(
        await apiRequest<IntegrationSettingsDto>('/admin/integrations/hubsoft', {
          method: 'PUT',
          body: { [qual]: '' },
        }),
      );
      setAviso('Segredo removido.');
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao remover');
    }
  }

  async function testar() {
    setTestando(true);
    setTeste(null);
    setErro(null);

    try {
      const resultado = await apiRequest<{ ok: boolean; erro: string | null }>(
        '/admin/integrations/hubsoft/test',
        { method: 'POST' },
      );
      setTeste(resultado);
      // O teste grava a data e o resultado; recarrega para o cabecalho refletir.
      const atualizado = await apiRequest<IntegrationSettingsDto>('/admin/integrations/hubsoft');
      setConfig(atualizado);
      onEstado?.(atualizado);
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao testar');
    } finally {
      setTestando(false);
    }
  }

  if (carregando) return <Spinner label="Carregando integração..." />;
  if (!config || !rascunho) {
    return <Alert>{erro ?? 'Não foi possível carregar a integração.'}</Alert>;
  }

  const usaSenha = rascunho.authType === 'password';

  return (
    <div className="space-y-6">
      {erro ? <Alert>{erro}</Alert> : null}
      {aviso ? <Alert tone="success">{aviso}</Alert> : null}

      <Card title="Situação">
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                config.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {config.isActive ? 'ativa' : 'desligada'}
            </span>
            <span className="text-slate-600">
              Último teste: {quando(config.lastCheckedAt)}
              {config.lastCheckOk === null
                ? ''
                : config.lastCheckOk
                  ? ' — conexão OK'
                  : ` — falhou: ${config.lastCheckError ?? 'sem detalhe'}`}
            </span>
          </div>

          {podeEscrever ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="ghost" onClick={() => void testar()} disabled={testando}>
                {testando ? 'Testando...' : 'Testar conexão'}
              </Button>
              <span className="text-xs text-slate-500">
                Emite um token com as credenciais gravadas. Funciona mesmo com a integração
                desligada — é assim que se confere antes de ligar.
              </span>
            </div>
          ) : null}

          {teste ? (
            teste.ok ? (
              <Alert tone="success">Conexão OK: o HubSoft aceitou as credenciais.</Alert>
            ) : (
              <Alert>{teste.erro ?? 'A conexão falhou.'}</Alert>
            )
          ) : null}
        </div>
      </Card>

      <Card title="Credenciais">
        <fieldset className="space-y-4" disabled={!podeEscrever}>
          <Field
            label="Endereço da API"
            value={rascunho.baseUrl}
            placeholder="https://SEUPROVEDOR.hubsoft.com.br"
            onChange={(e) => setCampo('baseUrl', e.target.value)}
            hint="Sem barra no final e sem /api/v1 — o sistema completa o caminho."
          />

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">
              Tipo de autenticação
            </span>
            <select
              value={rascunho.authType}
              onChange={(e) => setCampo('authType', e.target.value)}
              disabled={!podeEscrever}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
            >
              <option value="password">Usuário e senha (grant_type=password)</option>
              <option value="client_credentials">Só client_id e secret</option>
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              O HubSoft usa usuário e senha junto com o client_id e o secret.
            </span>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Client ID"
              value={rascunho.clientId}
              onChange={(e) => setCampo('clientId', e.target.value)}
            />

            <Segredo
              label="Client Secret"
              gravado={config.temClientSecret}
              valor={clientSecret}
              onChange={setClientSecret}
              onApagar={podeEscrever ? () => void apagarSegredo('clientSecret') : undefined}
            />

            {usaSenha ? (
              <>
                <Field
                  label="Usuário"
                  value={rascunho.username}
                  onChange={(e) => setCampo('username', e.target.value)}
                  hint="O mesmo usuário de API criado no HubSoft."
                />

                <Segredo
                  label="Senha"
                  gravado={config.temPassword}
                  valor={password}
                  onChange={setPassword}
                  onApagar={podeEscrever ? () => void apagarSegredo('password') : undefined}
                />
              </>
            ) : null}
          </div>
        </fieldset>
      </Card>

      <Card title="Comportamento">
        <fieldset className="grid gap-4 sm:grid-cols-2" disabled={!podeEscrever}>
          <Field
            label="Tempo limite (ms)"
            type="number"
            min={1000}
            max={30000}
            value={rascunho.timeoutMs}
            onChange={(e) => setCampo('timeoutMs', e.target.value)}
            hint="Quanto esperar pelo HubSoft antes de desistir. O cliente está na frente da tela."
          />

          <Field
            label="Cache da consulta (segundos)"
            type="number"
            min={0}
            max={3600}
            value={rascunho.cacheTtlSeconds}
            onChange={(e) => setCampo('cacheTtlSeconds', e.target.value)}
            hint="Evita repetir a consulta do mesmo CPF. Use 0 para consultar sempre."
          />

          <label className="flex items-start gap-3 sm:col-span-2">
            <input
              type="checkbox"
              checked={rascunho.isActive}
              onChange={(e) => setCampo('isActive', e.target.checked)}
              disabled={!podeEscrever}
              className="mt-0.5 size-4 shrink-0 accent-brand-600"
            />
            <span>
              <span className="block text-sm font-medium text-slate-800">Integração ativa</span>
              <span className="block text-xs text-slate-600">
                Desligada, o portal recusa todo mundo no modo CPF. Ligue depois que o teste de
                conexão passar.
              </span>
            </span>
          </label>
        </fieldset>
      </Card>

      <Card title="Classificação dos status de serviço">
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Por padrão, <strong>quem aparece na consulta entra</strong> — o HubSoft já não devolve
            cliente cancelado. Use esta tabela só para abrir exceção a um status específico.
          </p>

          {linhas.length === 0 ? (
            <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
              Nenhum status observado ainda. Eles aparecem sozinhos aqui conforme os clientes vão
              conectando — o primeiro cliente com aquele status ensina o rótulo ao sistema.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                    <th className="py-2 pr-4 font-medium">Status devolvido pelo HubSoft</th>
                    <th className="py-2 font-medium">Decisão</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((linha) => (
                    <tr key={linha.status} className="border-b border-slate-100">
                      <td className="py-2 pr-4">
                        <span className="font-mono text-xs">{linha.status}</span>
                        {linha.sugestao ? (
                          <span className="ml-2 text-xs text-slate-500">
                            (sugestão: {linha.sugestao === 'allow' ? 'liberar' : 'bloquear'})
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2">
                        <select
                          value={linha.decisao}
                          disabled={!podeEscrever}
                          onChange={(e) =>
                            setDecisao(linha.status, e.target.value as DecisaoDeStatus | '')
                          }
                          className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm"
                        >
                          <option value="">Sem opinião (a presença decide)</option>
                          <option value="allow">Liberar</option>
                          <option value="deny">Bloquear</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {podeEscrever ? (
        <div className="flex justify-end">
          <Button onClick={() => void salvar()} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      ) : null}

      <Card title="Depois de configurar">
        <p className="text-sm text-slate-600">
          A integração sozinha não muda nada para o cliente. Para o Wi-Fi passar a pedir CPF, vá
          em{' '}
          <Link to="/mikrotiks" className="font-semibold underline">
            MikroTiks
          </Link>{' '}
          → Portal do equipamento e escolha o modo <strong>CPF do cliente</strong>. Esse modo
          coleta dado pessoal, então exige um texto de{' '}
          <Link to="/termos" className="font-semibold underline">
            termos
          </Link>{' '}
          vigente.
        </p>
      </Card>
    </div>
  );

  function setCampo<C extends keyof Rascunho>(chave: C, valor: Rascunho[C]) {
    setAviso(null);
    setRascunho((atual) => (atual ? { ...atual, [chave]: valor } : atual));
  }

  function setDecisao(status: string, decisao: DecisaoDeStatus | '') {
    setAviso(null);
    setLinhas((atual) =>
      atual.map((linha) => (linha.status === status ? { ...linha, decisao } : linha)),
    );
  }
}

/**
 * Campo de segredo: sempre vazio ao abrir.
 *
 * Em branco significa manter o gravado — por isso apagar de verdade precisa de
 * um botao proprio, senao nao haveria como distinguir "nao mexi" de "quero
 * remover".
 */
function Segredo({
  label,
  gravado,
  valor,
  onChange,
  onApagar,
}: {
  label: string;
  gravado: boolean;
  valor: string;
  onChange: (valor: string) => void;
  onApagar?: () => void;
}) {
  return (
    <div>
      <Field
        label={label}
        type="password"
        autoComplete="new-password"
        value={valor}
        placeholder={gravado ? '•••••••• (gravado)' : 'não preenchido'}
        onChange={(e) => onChange(e.target.value)}
        hint={gravado ? 'Deixe em branco para manter o que está gravado.' : undefined}
      />
      {gravado && onApagar ? (
        <button type="button" onClick={onApagar} className="mt-1 text-xs text-slate-500 underline">
          Apagar o valor gravado
        </button>
      ) : null}
    </div>
  );
}
